import { type Accessor, createEffect, createSignal, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import './energy-particles.css'

interface Props {
  fire: Accessor<boolean>
}

interface Geometry {
  sx: number
  sy: number
  tx: number
  ty: number
  paths: [string, string, string]
  trails: { x: number; y: number }[]
}

function cubicBezier(
  t: number,
  p0: number,
  p1: number,
  p2: number,
  p3: number,
) {
  const u = 1 - t
  return (
    u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
  )
}

function computeGeometry(sourceRect: DOMRect, targetRect: DOMRect): Geometry {
  const sx = sourceRect.left + sourceRect.width / 2
  const sy = sourceRect.top + sourceRect.height / 2
  // Target the visible left portion of the progress bar (where the indicator typically is)
  // Use the fill element if available, otherwise target ~25% into the bar
  const fill = document.querySelector('[data-energy-target] [class*="from-"]')
  const fillWidth = fill
    ? parseFloat((fill as HTMLElement).style.width) || 25
    : 25
  const tx =
    targetRect.left + (targetRect.width * Math.min(fillWidth, 90)) / 100
  const ty = targetRect.top + targetRect.height / 2
  const dx = tx - sx
  const dy = ty - sy

  // Control point ratios derived from the reference animation
  const paths: [string, string, string] = [
    `M 0 0 C ${dx * 0.69} ${dy * 0.22}, ${dx * 0.94} ${dy * 0.71}, ${dx} ${dy}`,
    `M 0 0 C ${dx * 0.54} ${dy * 0.16}, ${dx * 1.07} ${dy * 0.76}, ${dx} ${dy}`,
    `M 0 0 C ${dx * 0.4} ${dy * 0.12}, ${dx * 1.14} ${dy * 0.79}, ${dx} ${dy}`,
  ]

  // Sample 5 trail dots along the first bezier curve
  const cp1x = dx * 0.69
  const cp1y = dy * 0.22
  const cp2x = dx * 0.94
  const cp2y = dy * 0.71
  const tValues = [0.15, 0.3, 0.45, 0.6, 0.75]
  const trails = tValues.map((t) => ({
    x: sx + cubicBezier(t, 0, cp1x, cp2x, dx),
    y: sy + cubicBezier(t, 0, cp1y, cp2y, dy),
  }))

  return { sx, sy, tx, ty, paths, trails }
}

export function EnergyParticles(props: Props) {
  const [geo, setGeo] = createSignal<Geometry | null>(null)
  const [animating, setAnimating] = createSignal(false)

  function trigger() {
    const source = document.querySelector('[data-energy-source]')
    const target = document.querySelector('[data-energy-target]')
    if (!source || !target) return

    const g = computeGeometry(
      source.getBoundingClientRect(),
      target.getBoundingClientRect(),
    )
    setGeo(g)

    requestAnimationFrame(() => {
      setAnimating(true)
      setTimeout(() => {
        setAnimating(false)
        setGeo(null)
      }, 1800)
    })
  }

  createEffect(() => {
    if (!props.fire()) return
    trigger()
  })

  return (
    <Show when={geo()}>
      {(g) => {
        const fire = () => (animating() ? 'ep-fire' : '')
        return (
          <Portal>
            <div class="ep-overlay">
              {/* Particles */}
              <div
                class={`ep-particle ep-p1 ${fire()}`}
                style={{
                  left: `${g().sx}px`,
                  top: `${g().sy}px`,
                  'offset-path': `path('${g().paths[0]}')`,
                }}
              />
              <div
                class={`ep-particle ep-p2 ${fire()}`}
                style={{
                  left: `${g().sx}px`,
                  top: `${g().sy}px`,
                  'offset-path': `path('${g().paths[1]}')`,
                }}
              />
              <div
                class={`ep-particle ep-p3 ${fire()}`}
                style={{
                  left: `${g().sx}px`,
                  top: `${g().sy}px`,
                  'offset-path': `path('${g().paths[2]}')`,
                }}
              />

              {/* Trail dots */}
              <div
                class={`ep-trail ep-t1 ${fire()}`}
                style={{
                  left: `${g().trails[0].x}px`,
                  top: `${g().trails[0].y}px`,
                }}
              />
              <div
                class={`ep-trail ep-t2 ${fire()}`}
                style={{
                  left: `${g().trails[1].x}px`,
                  top: `${g().trails[1].y}px`,
                }}
              />
              <div
                class={`ep-trail ep-t3 ${fire()}`}
                style={{
                  left: `${g().trails[2].x}px`,
                  top: `${g().trails[2].y}px`,
                }}
              />
              <div
                class={`ep-trail ep-t4 ${fire()}`}
                style={{
                  left: `${g().trails[3].x}px`,
                  top: `${g().trails[3].y}px`,
                }}
              />
              <div
                class={`ep-trail ep-t5 ${fire()}`}
                style={{
                  left: `${g().trails[4].x}px`,
                  top: `${g().trails[4].y}px`,
                }}
              />

              {/* Burst rings at target */}
              <div
                class={`ep-burst ep-burst-1 ${fire()}`}
                style={{ left: `${g().tx}px`, top: `${g().ty}px` }}
              />
              <div
                class={`ep-burst ep-burst-2 ${fire()}`}
                style={{ left: `${g().tx}px`, top: `${g().ty}px` }}
              />
              <div
                class={`ep-burst ep-burst-3 ${fire()}`}
                style={{ left: `${g().tx}px`, top: `${g().ty}px` }}
              />
            </div>
          </Portal>
        )
      }}
    </Show>
  )
}
